/* eslint-disable no-case-declarations,no-unreachable */
import {useEffect, useMemo, useState} from 'react';

import {oneOfType, node, func} from 'prop-types';
import {useForm} from 'react-hook-form';

import {
  API_ENDPOINTS,
  CONNECTOR_LOGIN_PROCESS_TYPE_LOCALSTORAGE_KEY,
  CONNECTORS,
  fulllOauthCallbackRoute,
  pennylaneOauthCallbackRoute,
  PROJECTS_GROUPING_TYPES,
  PROJECTS_VIEW_TYPES,
  REGULAR_EXPRESSIONS,
  SNACKBAR_ACTIONS,
  tiimeOauthCallbackRoute,
  INTEGRATION_JOB_TYPES,
  DATA_INTEGRATION_JOBS_ACTIVE_STATUSES,
  DATA_INTEGRATION_JOBS_STATUSES
} from '../const';
import ProjectsContext from '../contexts/ProjectsContext';
import useHttp from '../hooks/misc/useHttp';
import {usePersistedReducer} from '../hooks/misc/usePersistedReducer';
import usePayment from '../hooks/providers/usePayment';
import useReport from '../hooks/providers/useReport';
import useSnackbar from '../hooks/providers/useSnackbar';
import useWorkspaces from '../hooks/providers/useWorkspaces';
import {ActionCreators, initialState, reducer} from '../reducers/providers/projectsReducer';
import {generateExternalConnectorLoginRedirectUrl, getFileB64, isDemoProject, sortProjectsAlphabetically} from '../utils';

const ProjectsProvider = ({children}) => {
  const paymentContext = usePayment();
  const reportsContext = useReport();
  const {shouldRegenerateToken, setShouldRegenerateToken, loadReports} = reportsContext;
  const {isDefaultWorkspace, selectedWorkspace} = useWorkspaces();
  const {_post, _get} = useHttp();
  const {showSnackbar, closeSnackbar, setSnackbars, defaultSnackbarOptions, snackbars} = useSnackbar();

  const [oauthError, setOauthError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createProjectApiKeyError, setCreateProjectApiKeyError] = useState('');
  const [updateProjectUsersSuccess, setUpdateProjectUsersSuccess] = useState(false);
  const [createProjectUsername, setCreateProjectUsername] = useState('');
  const [createProjectPassword, setCreateProjectPassword] = useState('');
  const [createProjectApiKey, setCreateProjectApiKey] = useState('');
  const [importDataFileError, setImportDataFileError] = useState('');
  const [selectedReportTab, setSelectedReportTab] = useState();
  const [createProjectSiren, setCreateProjectSiren] = useState('');
  const [isSirenValid, setIsSirenValid] = useState(false);
  const [projectCreated, setProjectCreated] = useState(false);
  const [isCegidFormValid, setIsCegidFormValid] = useState(false);
  const [createProjectSirenError, setCreateProjectSirenError] = useState('');
  const [shouldFetchProjects, setShouldFetchProjects] = useState(true);
  const [searchedConnectionTerm, setSearchedConnectionTerm] = useState('');
  const [createConnectionFormStep, setCreateConnectionFormStep] = useState(0);

  const connectionsReducerKey = 'CONNECTION_REDUCER_KEY';
  const {state, dispatch} = usePersistedReducer(reducer, initialState, connectionsReducerKey);
  const {projects, emailError, isSubmitting, userInvitedSuccessMessage, deleteUserModalOpen, userToDelete, promoteModalOpen, userToPromote} = state;

  const {
    addUser: addUserActionCreator,
    setDeletedSuccessfully,
    setDeleteUserModalOpen,
    setUserToDelete,
    setPromoteModalOpen,
    setUserToPromote,
    promoteOrDemoteUser: promoteOrDemoteUserActionCreator,
    deleteUser: deleteUserActionCreator,
    setAddedSuccessfully,
    setUserInvitedSuccessMessage,
    setEmailError,
    setIsSubmitting,
    setConnections,
    deleteConnection,
    deleteConnectionSuccess,
    deleteConnectionFail,
    updateConnection,
    updateConnectionFail,
    updateConnectionSuccess,
    refreshConnection,
    refreshConnectionSuccess,
    jobPolled,
    jobRegistered,
    jobHandled
  } = ActionCreators(dispatch);

  const [projectsViewType, setProjectsViewType] = useState(PROJECTS_VIEW_TYPES.card);
  const [connectionGroupingType, setConnectionGroupingType] = useState(PROJECTS_GROUPING_TYPES.folder);
  const [deleteProjectModalOpen, setDeleteProjectModalOpen] = useState(false);
  const [updateProjectModalOpen, setUpdateProjectModalOpen] = useState(false);
  const [manageProjectUsersModalOpen, setManageProjectUsersModalOpen] = useState(false);
  const [projectCardMenuAnchorEl, setProjectCardMenuAnchorEl] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isProjectCreationSubmitting, setIsProjectCreationSubmitting] = useState(false);
  const [connector, setConnector] = useState(null);
  const [actualizeProjectDataDatePickerOpen, setActualizeProjectDataDatePickerOpen] = useState(false);
  const [isActualizeProjectMenuOpen, setIsActualizeProjectMenuOpen] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [collapsedCategories, setCollapsedCategories] = useState([]);
  const [loginToConnectorFormData, setLoginToConnectorFormData] = useState({});
  const [isLoggingToConnector, setIsLoggingToConnector] = useState(false);
  const [isSuccessfullyLoggedInToConnector, setIsSuccessfullyLoggedInToConnector] = useState(false);
  const [connectorFolders, setConnectorFolders] = useState([]);
  const [foldersCompanyIdsToConnect, setFoldersCompanyIdsToConnect] = useState([]);
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [isConnectorsLoading, setIsConnectorsLoading] = useState(false);
  const [isRedirectingToConnectorAuthenticationServer, setIsRedirectingToConnectorAuthenticationServer] = useState(false);
  const [isGeneratingConnectorToken, setIsGeneratingConnectorToken] = useState(false);
  const [isChooseLoginToConnectorProcessModalOpen, setIsChooseLoginToConnectorProcessModalOpen] = useState(false);
  const [maximumNumberOfProjectsReachedModalOpen, setMaximumNumberOfProjectsReachedModalOpen] = useState(false);
  // const [projectsCurrentlyRefreshing, setProjectsCurrentlyRefreshing] = useState([]);

  // When a custom report is deleted, we have to set a new selected tab within current workspace.
  // This hook aims to automatically set a new selected tab after custom report deletion.
  useEffect(() => {
    if (!isDefaultWorkspace && selectedWorkspace.reports.length > 0) {
      setSelectedReportTab(selectedWorkspace.reports[0].report_id);
    }
  }, [selectedWorkspace.reports]);

  useEffect(() => {
    (async () => {
      if (shouldRegenerateToken) {
        await loadReports();
        setShouldRegenerateToken(false);
      }
    })();
  }, [shouldRegenerateToken]);

  const checkSirenValidity = sirenInput => {
    setIsSirenValid(REGULAR_EXPRESSIONS.siren.test(sirenInput));
  };

  const handleSirenChange = e => {
    const sirenWithoutSpaces = e.target.value.replaceAll(' ', '');
    setCreateProjectSiren(sirenWithoutSpaces);
    checkSirenValidity(sirenWithoutSpaces);
  };

  const isSirenAlreadyTakenForConnector = (siren, conn) => {
    let createdProject;
    if (conn.is_manual) {
      const compatibleEditorsIds = conn.compatible_editors.map(e => e.id);
      createdProject = projects.find(p => p.siren === siren && compatibleEditorsIds.includes(p.connector));
    } else {
      createdProject = projects.find(p => p.siren === siren && p.connector === conn.id);
    }

    return createdProject !== undefined;
  };

  const fetchProjects = async () => {
    setIsLoading(true);

    const url = API_ENDPOINTS.projects.findAll;
    try {
      const {response: projectsResponse, responseJson: data} = await _get(url);

      if (projectsResponse.ok) {
        const projectsWithStringifiedSirens = data.map(p => ({...p, siren: p.siren.toString()}));
        setConnections(sortProjectsAlphabetically(projectsWithStringifiedSirens));

        setIsLoading(false);
      } else {
        // eslint-disable-next-line no-console
        console.error({projectsResponse});
        if (projectsResponse.status === 401) {
          setShouldFetchProjects(true);
        }
        throw Error('Error during projects fecthing');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const initOauthLoginToConnector = async (connectorId, redirectUri, form) => {
    try {
      const url = API_ENDPOINTS.projects.initOauthLoginToConnector;

      const {response, responseJson: data} = await _post(url, {
        connector: connectorId,
        redirect_uri: redirectUri,
        form: form || null
      });

      if (response.status === 200) {
        return {
          success: true,
          status: 200,
          data
        };
      }

      // API call has errored, we reset to connectors selection page
      setCreateConnectionFormStep(0);

      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const getConnectorAppCallbackRoute = connectorId => {
    if (connectorId === CONNECTORS.tiime) {
      return tiimeOauthCallbackRoute;
    }

    if (connectorId === CONNECTORS.pennylane) {
      return pennylaneOauthCallbackRoute;
    }

    if (connectorId === CONNECTORS.fulll) {
      return fulllOauthCallbackRoute;
    }
  };

  const getRedirectUri = connectorId => {
    const appRoute = getConnectorAppCallbackRoute(connectorId);
    const baseUrl = window.location.origin.toString();
    const finalUrl = `${baseUrl}${appRoute}`;
    console.log({finalUrl});
    return finalUrl;
  };

  const redirectToConnectorLoginPage = async (connectorId, options = {}) => {
    setIsRedirectingToConnectorAuthenticationServer(true);

    const redirectUriAfterSuccess = getRedirectUri(connectorId);
    console.log({redirectUriAfterSuccess});
    const res = await initOauthLoginToConnector(connectorId, redirectUriAfterSuccess, options.formId);
    if (res.success) {
      const loginUrl = generateExternalConnectorLoginRedirectUrl(connectorId, {
        ...options,
        state: res.data
      });
      setTimeout(() => setIsRedirectingToConnectorAuthenticationServer(false), 10000);
      window.location.href = loginUrl;
      return;
    }
    setIsRedirectingToConnectorAuthenticationServer(false);
  };

  const checkConnectorConnection = async connectorId => {
    try {
      setConnectorFolders([]);
      const url = API_ENDPOINTS.projects.verifyConnection;
      const {response, responseJson: data} = await _post(url, {
        connector: connectorId
      });

      if (response.status === 400 && connectorId === CONNECTORS.tiime) {
        redirectToConnectorLoginPage(connectorId);
        return;
      }

      // Connector has many ways to connect to external data (OAuth, api keys ...), we open the choice modal
      if (response.status === 400 && connector.forms?.length > 1) {
        setIsChooseLoginToConnectorProcessModalOpen(true);
        return;
      }

      // Connector has only one way to connect to external data, we directly perform relevant action
      if (response.status === 400 && connector.forms?.length === 1) {
        const loginProcess = connector.forms[0];
        // OAuth way
        if (loginProcess.oauth) {
          redirectToConnectorLoginPage(connectorId, {
            scope: loginProcess.scopes,
            clientId: loginProcess.client_id,
            formId: loginProcess.id
          });
        }
        return;
      }

      if (response.status === 200) {
        setIsSuccessfullyLoggedInToConnector(true);
        setConnectorFolders(data);
      }

      return {
        success: response.status === 200,
        data
      };
    } catch (e) {
      return {
        success: false,
        message: e.message
      };
    }
  };

  const loginToConnector = async (connectorId, credentials) => {
    try {
      setConnectorFolders([]);
      const url = API_ENDPOINTS.projects.loginToConnector;
      const {response, responseJson: data} = await _post(url, {
        connector: connectorId,
        ...credentials
      });

      if (response.status === 200) {
        setConnectorFolders(data.companies);
      }

      return {
        success: response.status === 200,
        data
      };
    } catch (e) {
      return {
        success: false,
        message: e.message
      };
    }
  };

  const createConnection = async (conn, companies, files = []) => {
    try {
      const url = API_ENDPOINTS.projects.connectFolders;
      const formattedFiles = [];

      // eslint-disable-next-line no-restricted-syntax
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        const b64 = await getFileB64(f);
        const formattedFileForApi = b64.split('base64,')[1];

        formattedFiles.push({
          filename: f.name,
          file_b64: formattedFileForApi
        });
      }

      const {response, responseJson: data} = await _post(url, {
        ...(files.length > 0 ? {files: formattedFiles} : {}),
        connector: conn.id,
        companies,
        months: 3 // TODO Backend is not ready yet
      });

      if (response.status === 202) {
        setCreateProjectModalOpen(false);
        jobRegistered({
          ...data,
          type: INTEGRATION_JOB_TYPES.MASS_IMPORT,
          projects: companies,
          schema: connector.schema_name
        });
      }

      if (response.status === 400) {
        showSnackbar(data, {severity: 'error', duration: 5000});
      }

      return {
        success: response.status === 200,
        data
      };
    } catch (e) {
      return {
        success: false,
        message: e.message
      };
    }
  };

  const updateProject = async project => {
    const {siren, logo = null, name, month = null, schema_name: schema} = project;

    try {
      const integerMonth = parseInt(month, 10);

      const url = API_ENDPOINTS.projects.update;
      const snackbarId = showSnackbar(SNACKBAR_ACTIONS.UPDATE_PROJECT_IN_PROGRESS, {
        severity: 'info',
        autoHide: false
      });

      updateConnection(project);

      const {response, responseJson: data} = await _post(url, {
        siren,
        url_logo: logo,
        project_name: name,
        first_fiscal_month: integerMonth,
        schema
      });

      closeSnackbar(snackbarId);

      if (response.status === 200) {
        updateConnectionSuccess({
          ...project,
          siren,
          url_logo: logo,
          project_name: name,
          first_fiscal_month: integerMonth,
          schema
        });
        const projectBeforeUpdate = projects.find(p => p.siren === siren);
        const firstFiscalMonthHasChanged = integerMonth !== projectBeforeUpdate.first_fiscal_month;
        showSnackbar(SNACKBAR_ACTIONS.UPDATE_PROJECT_SUCCESS, defaultSnackbarOptions, {firstFiscalMonthHasChanged});

        return {
          success: true,
          message: data?.message || data,
          status: 200
        };
      }
      updateConnectionFail(project);
      showSnackbar(data?.message || data, {severity: 'error'});
      return {success: false};
    } catch (e) {
      return {
        message: e.message
      };
    }
  };

  const addUserToProject = async (userData, shouldSendInvitationEmail) => {
    try {
      const {email, siren, schema} = userData;

      addUserActionCreator();
      const url = API_ENDPOINTS.projects.addUser;
      const {response, responseJson: data} = await _post(url, {
        guest_usermail: email,
        siren,
        schema,
        send_mail: shouldSendInvitationEmail
      });

      if (response.status === 200) {
        showSnackbar(SNACKBAR_ACTIONS.ADD_USER_SUCCESS, defaultSnackbarOptions, {newUserUsername: data.givenName});

        const user = {
          username: data.givenName,
          user_id: data.id,
          mail: data.mail
        };
        setAddedSuccessfully({siren, schema, user});
      }

      if (response.status === 402) {
        setUserInvitedSuccessMessage(data);
        setEmailError('');

        const user = {
          username: email, // Before full inscription, we do not have username so we use email
          user_id: `pending-user=${email}`
        };
        setAddedSuccessfully({siren, schema, user});
      }

      return {
        status: response.status,
        success: response.status === 200 || response.status === 402
      };
    } catch (e) {
      return {
        message: e
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteUserFromProject = async (userData, isUserDeletingHimself = false) => {
    try {
      const {userId, siren, schema} = userData;
      deleteUserActionCreator();
      const url = API_ENDPOINTS.projects.deleteUser;
      const {response, responseJson: data} = await _post(url, {
        delete_user_id: userId,
        siren,
        schema
      });

      if (response.status === 200) {
        // We have to remove project from projects list if user deleted himself
        if (isUserDeletingHimself) {
          const connection = {
            siren,
            schema_name: schema
          };
          deleteConnectionSuccess(connection);
        } else {
          setDeletedSuccessfully({siren, schema, userId});
        }
        showSnackbar(SNACKBAR_ACTIONS.DELETE_USER_SUCCESS);

        return {
          message: data,
          status: 200
        };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  const promoteOrDemoteUser = async userData => {
    const {siren, userId, isOwner, schema} = userData;
    try {
      const url = API_ENDPOINTS.users.promoteOrDemote;
      setIsSubmitting(true);
      const {response, responseJson: data} = await _post(url, {
        siren,
        owner_id: userId,
        is_owner: isOwner,
        schema
      });
      setIsSubmitting(false);

      const project = projects.find(p => p.siren === siren && p.schema_name === schema);

      const {username} = project.users.find(u => u.user_id === userId);

      if (response.status === 200) {
        promoteOrDemoteUserActionCreator({siren, schema, userId});
        showSnackbar(isOwner ? SNACKBAR_ACTIONS.PROMOTE_USER_SUCCESS : SNACKBAR_ACTIONS.DEMOTE_USER_SUCCESS, defaultSnackbarOptions, {
          promotedUserEmail: isOwner ? username : undefined,
          demotedUserEmail: !isOwner ? username : undefined
        });
        return {
          message: data,
          status: response.status
        };
      }
      if (response.status === 400) {
        showSnackbar(data, {severity: 'error', duration: 4000});
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e
      };
    }
  };

  const promoteOrDemote = async (userId, isPromoting) => {
    const {siren, schema_name: schema} = selectedProject;
    const data = {
      userId,
      isOwner: isPromoting ? 1 : 0,
      siren,
      schema
    };
    return promoteOrDemoteUser(data);
  };

  const deleteProject = async project => {
    try {
      const schema = project.schema_name;
      const url = API_ENDPOINTS.projects.delete;
      const snackbarId = showSnackbar(
        SNACKBAR_ACTIONS.DELETE_PROJECT_IN_PROGRESS,
        {
          severity: 'info',
          autoHide: false
        },
        {deletedConnectionName: project.project_name}
      );

      deleteConnection(project);

      const {response, responseJson: data} = await _post(url, {
        siren: project.siren,
        schema: isDefaultWorkspace ? schema : selectedWorkspace.schema_name,
        report_id: project.report_id
      });

      if (response.status !== 200) {
        deleteConnectionFail(project);
        closeSnackbar(snackbarId);
        showSnackbar(data?.message || data, {severity: 'error', duration: 4000});
      } else {
        deleteConnectionSuccess(project);
        paymentContext.getUserSubscriptionStatus(true);

        // Business rule : Projects created within custom workspace are not counted on creation
        // So we must not decrement projects counter when deleting a custom ws project.
        // PS : only custom ws projects have a report_id
        // Also, demo projects should not trigger decrementation

        if (!project.report_id && !isDemoProject(project)) {
          paymentContext.decrementNumberOfProjects();
        }

        // Business rule: If projects.length passes from 0 to 1 , or from 1 to 0 in a given module, we should regenerate token
        const numberOfProjectInSchemaBefore = projects.filter(p => p.schema_name === schema).length;
        const numberOfProjectInSchemaAfter = numberOfProjectInSchemaBefore - 1;

        if (numberOfProjectInSchemaBefore === 1 && numberOfProjectInSchemaAfter === 0) {
          setShouldRegenerateToken(true);
        }

        closeSnackbar(snackbarId);
        showSnackbar(SNACKBAR_ACTIONS.DELETE_PROJECT_SUCCESS, defaultSnackbarOptions, {deletedConnectionName: project.project_name});

        return {
          status: 200
        };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const importDataFile = async (project, file) => {
    try {
      const b64 = await getFileB64(file);
      const formattedFileForApi = b64.split('base64,')[1];

      const url = API_ENDPOINTS.projects.import;
      const {response, responseJson: data} = await _post(url, {
        filename: file.name,
        file_b64: formattedFileForApi,
        siren: project.siren,
        schema: project.schema_name,
        connector: project.connector
      });

      return {
        status: response.status,
        success: response.status === 200
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setImportDataFileError(e.message);
      return {
        message: e.message
      };
    }
  };

  const manualRefreshProject = async (project, date = null) => {
    try {
      const url = API_ENDPOINTS.projects.refresh.manual;

      refreshConnection(project);

      const {response, responseJson: data} = await _post(url, {
        siren: project.siren,
        schema: project.schema_name,
        ...(date !== null && {pivot_date: date})
      });

      if (response.status === 202 && data.job_id) {
        jobRegistered({
          ...data,
          type: INTEGRATION_JOB_TYPES.REFRESH,
          projects: [{name: project.project_name, siren: project.siren}],
          schema: project.schema_name
        });

        return {
          success: true,
          jobId: data.job_id
        };
      }

      return {success: false};
    } catch (e) {
      refreshConnectionSuccess(project);

      console.error(e);
      return {message: e.message};
    }
  };

  const enableOrDisableProjectAutoRefresh = async project => {
    try {
      const url = API_ENDPOINTS.projects.refresh.enableDisableAuto;
      const {response} = await _post(url, {
        siren: project.siren,
        auto_update: project.auto_update ? 0 : 1,
        schema: project.schema_name
      });

      if (response.status !== 200 && response.status !== 201) {
        return {
          success: false,
          status: response.status
        };
      }
      await fetchProjects();
      return {
        status: 200,
        message: 'success'
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const handleJobCompleted = async job => {
    const data = job.lastPollData;

    const numberOfProjectInSchemaBefore = projects.filter(p => p.schema_name === job.schema).length;
    const numberOfProjectInSchemaAfter = numberOfProjectInSchemaBefore + job.total_companies;

    // --- Token regeneration ---
    if (numberOfProjectInSchemaBefore === 0 && numberOfProjectInSchemaAfter > 0) {
      setShouldRegenerateToken(true);
    }

    // --- Refresh successful folders ---
    const integratedFoldersSirens = data.results.filter(r => r.successful).map(r => r.siren);
    const integratedFoldersNames = job.projects.map(p => p.name);
    integratedFoldersSirens.forEach(s => refreshConnectionSuccess({siren: s, schema_name: job.schema}));

    // --- Update number of projects ---
    if (job.type === INTEGRATION_JOB_TYPES.MASS_IMPORT) {
      paymentContext.incrementNumberOfProjects(job.total_companies);
    }

    // --- Update snackbars ---
    setSnackbars(current => current.filter(s => s.action !== SNACKBAR_ACTIONS.CREATE_CONNECTION_DATA_INTEGRATION_IN_PROGRESS));
    showSnackbar(SNACKBAR_ACTIONS.CREATE_CONNECTION_DATA_INTEGRATION_SUCCESS, defaultSnackbarOptions, {integratedFoldersNames});

    // --- Conditional refetch projects and projects authorizations ---
    if (job.type === INTEGRATION_JOB_TYPES.MASS_IMPORT) {
      paymentContext.getUserSubscriptionStatus(true);
      await fetchProjects();
    }
  };

  const pollIntegrationJob = async job => {
    try {
      const {responseJson: data} = await _post(API_ENDPOINTS.projects.newConnectionDataIntegrationStatus, {job_id: job.job_id}, {shouldDisplayErrorSnackbar: false});
      jobPolled(data);
    } catch (e) {
      console.error(e);
    }
  };

  const getConnectors = async () => {
    try {
      if (connectors.length > 0) return;

      setIsConnectorsLoading(true);
      const url = API_ENDPOINTS.projects.connectors;
      const {response, responseJson: data} = await _get(url);

      if (response.status === 200) {
        setConnectors(data);
        return {
          success: true,
          status: 200,
          data
        };
      }
      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    } finally {
      setIsConnectorsLoading(false);
    }
  };

  const deleteEditorConnection = async connectorId => {
    try {
      const url = API_ENDPOINTS.projects.deleteEditorConnection;

      const snackbarId = showSnackbar(SNACKBAR_ACTIONS.DELETE_EDITOR_CONNECTION, {
        severity: 'info',
        autoHide: false
      });

      const {response, responseJson: data} = await _post(url, {
        connector: connectorId
      });

      closeSnackbar(snackbarId);
      if (response.status === 200) {
        showSnackbar(SNACKBAR_ACTIONS.DELETE_EDITOR_CONNECTION_SUCCESS);

        return {
          success: true,
          status: 200,
          data
        };
      }
      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const currentReport = selectedWorkspace?.reports?.find(r => r.report_id === selectedReportTab) || {};

  const closeProjectMenu = () => {
    setProjectCardMenuAnchorEl(null);
    setSelectedProject(null);
  };

  const isLoginToConnectorFormValid = () => {
    let isValid = true;

    const selectedForm = localStorage.getItem(CONNECTOR_LOGIN_PROCESS_TYPE_LOCALSTORAGE_KEY);
    const credentialFieldsToDisplay = connector.forms ? connector.credential_fields.filter(f => f.form_id === selectedForm) : connector.credential_fields;

    credentialFieldsToDisplay.forEach(f => {
      if (!loginToConnectorFormData[f.id]) {
        isValid = false;
      }
    });
    return isValid;
  };

  const isProjectCurrentlyUpdating = project => {
    const id = project.siren + project.schema_name;
    const isUpdating = state.updating.find(item => item === id) !== undefined;
    return isUpdating;
  };

  const isProjectCurrentlyRefreshing = project => {
    const id = project.siren + project.schema_name;
    const isRefreshing = state.refreshing.find(item => item === id) !== undefined;
    return isRefreshing;
  };

  const isProjectCurrentlyDeleting = project => {
    const id = project.siren + project.schema_name;
    const isDeleting = state.deleting.find(item => item === id) !== undefined;
    return isDeleting;
  };

  const openDeleteUserModal = userId => {
    setUserToDelete(userId);
    setDeleteUserModalOpen(true);
  };

  const closeDeleteUserModal = () => {
    setUserToDelete(null);
    setDeleteUserModalOpen(false);
  };

  const openPromoteUserModal = userId => {
    setUserToPromote(userId);
    setPromoteModalOpen(true);
  };

  const closePromoteUserModal = () => {
    setUserToPromote(null);
    setPromoteModalOpen(false);
  };

  const getConnectorAssociatedToProject = project => {
    // First we find a connector in the list that matches the project.connector id
    const parentConnector = connectors.find(c => c.id === project.connector);
    if (parentConnector && !parentConnector.is_manual) return parentConnector;

    // If we do not find a connector matching the project.id, then it might be a manual connector.
    // Manual connectors hold a list of true connectors linked to the project inside the property connector.compatible_editors
    const manualConnector = connectors.find(c => c.id === project.schema_name);
    if (manualConnector) {
      return manualConnector.compatible_editors.find(c => c.id === project.connector);
    }

    return null;
  };

  const getAllManualConnectors = parentConnectors => {
    const manualParentConnectors = parentConnectors.filter(c => c.is_manual);
    return manualParentConnectors
      .map(parentConnector => {
        return parentConnector.compatible_editors.map(childConnector => childConnector);
      })
      .flat();
  };

  const MANUAL_CONNECTORS = connectors.length > 0 ? getAllManualConnectors(connectors) : [];
  const MANUAL_CONNECTORS_IDS = MANUAL_CONNECTORS.map(c => c.id);

  const CONNECTORS_HAVING_IMPORT_DATA_BUTTON = MANUAL_CONNECTORS_IDS;
  const CONNECTORS_WITHOUT_ACTUALIZE_DATA_BUTTON = MANUAL_CONNECTORS_IDS;
  const CONNECTORS_HAVING_UPLOAD_FEATURE = MANUAL_CONNECTORS_IDS;

  useEffect(() => {
    const completedUnhandledJobs = Object.values(state.integrationJobs).filter(job => job.status === DATA_INTEGRATION_JOBS_STATUSES.COMPLETED && !job.handled);

    const handleAllJobs = async () => {
      // eslint-disable-next-line no-restricted-syntax
      for (const job of completedUnhandledJobs) {
        // eslint-disable-next-line no-await-in-loop
        await handleJobCompleted(job);
        jobHandled(job.job_id);
      }
    };

    handleAllJobs();
  }, [state.integrationJobs]);

  useEffect(() => {
    const activeJobs = Object.values(state.integrationJobs).filter(job => !job.handled && DATA_INTEGRATION_JOBS_ACTIVE_STATUSES.includes(job.status));

    console.log({activeJobs});
    if (activeJobs.length === 0) {
      // remove progress snackbar if no active jobs
      setSnackbars(current => current.filter(s => s.action !== SNACKBAR_ACTIONS.CREATE_CONNECTION_DATA_INTEGRATION_IN_PROGRESS));
      return;
    }

    // --- calculate total / completed across all active jobs ---
    const completed = activeJobs.reduce((sum, job) => sum + (job.completedCount || 0), 0);
    const total = activeJobs.reduce((sum, job) => sum + (job.totalCount || 0), 0);
    console.log({activeJobs, completed, total});

    // --- keep only one progress snackbar ---
    setSnackbars(current => current.filter(s => s.action !== SNACKBAR_ACTIONS.CREATE_CONNECTION_DATA_INTEGRATION_IN_PROGRESS));

    showSnackbar(
      SNACKBAR_ACTIONS.CREATE_CONNECTION_DATA_INTEGRATION_IN_PROGRESS,
      {
        severity: 'info',
        autoHide: false
      },
      {
        numberOfIntegratedFolders: completed,
        totalNumbersOfFoldersToIntegrate: total
      }
    );
  }, [state.integrationJobs]);

  const memoizedValues = {
    projects,
    fetchProjects,
    updateProject,
    deleteProject,
    addUserToProject,
    deleteUserFromProject,
    updateProjectUsersSuccess,
    setUpdateProjectUsersSuccess,
    createError,
    createProjectApiKeyError,
    setCreateProjectApiKeyError,
    setCreateError,
    isLoading,
    setIsLoading,
    importDataFileError,
    setImportDataFileError,
    importDataFile,
    promoteOrDemoteUser,
    promoteOrDemote,
    selectedReportTab,
    setSelectedReportTab,
    siren: createProjectSiren,
    setSiren: setCreateProjectSiren,
    isSirenValid,
    handleSirenChange,
    isSirenAlreadyTakenForConnector,
    projectCreated,
    setProjectCreated,
    isCegidFormValid,
    setIsCegidFormValid,
    createProjectSirenError,
    setCreateProjectSirenError,
    shouldFetchProjects,
    setShouldFetchProjects,
    username: createProjectUsername,
    setUsername: setCreateProjectUsername,
    password: createProjectPassword,
    setPassword: setCreateProjectPassword,
    apiKey: createProjectApiKey,
    setApiKey: setCreateProjectApiKey,
    manualRefreshProject,
    enableOrDisableProjectAutoRefresh,
    integrationJobs: state.integrationJobs,
    projectsCurrentlyRefreshing: state.refreshing,
    newConnectionDataIntegrationInProgress: state.integrationJobs.length > 0,
    pollIntegrationJob,
    viewType: projectsViewType,
    setViewType: setProjectsViewType,
    deleteProjectModalOpen,
    setDeleteProjectModalOpen,
    updateProjectModalOpen,
    setUpdateProjectModalOpen,
    manageProjectUsersModalOpen,
    setManageProjectUsersModalOpen,
    projectCardMenuAnchorEl,
    setProjectCardMenuAnchorEl,
    selectedProject,
    setSelectedProject,
    currentReport,
    isSubmitting: isProjectCreationSubmitting,
    setIsSubmitting: setIsProjectCreationSubmitting,
    connector,
    setConnector,
    actualizeProjectDataDatePickerOpen,
    setActualizeProjectDataDatePickerOpen,
    closeProjectMenu,
    isActualizeProjectMenuOpen,
    setIsActualizeProjectMenuOpen,
    getConnectors,
    connectors,
    connectionGroupingType,
    setConnectionGroupingType,
    collapsedCategories,
    setCollapsedCategories,
    loginToConnectorFormData,
    setLoginToConnectorFormData,
    isLoginToConnectorFormValid,
    checkConnectorConnection,
    isLoggingToConnector,
    setIsLoggingToConnector,
    loginToConnector,
    isSuccessfullyLoggedInToConnector,
    setIsSuccessfullyLoggedInToConnector,
    connectorFolders,
    foldersCompanyIdsToConnect,
    setFoldersCompanyIdsToConnect,
    createConnection,
    createProjectModalOpen,
    setCreateProjectModalOpen,
    isConnectorsLoading,
    isRedirectingToConnectorAuthenticationServer,
    setIsRedirectingToConnectorAuthenticationServer,
    maximumNumberOfProjectsReachedModalOpen,
    setMaximumNumberOfProjectsReachedModalOpen,
    isGeneratingConnectorToken,
    setIsGeneratingConnectorToken,
    isProjectCurrentlyUpdating,
    isProjectCurrentlyDeleting,
    searchedConnectionTerm,
    setSearchedConnectionTerm,
    emailError,
    setEmailError,
    isUserActionSubmitting: isSubmitting,
    userInvitedSuccessMessage,
    deleteUserModalOpen,
    userToDelete,
    promoteModalOpen,
    userToPromote,
    openDeleteUserModal,
    closeDeleteUserModal,
    openPromoteUserModal,
    closePromoteUserModal,
    getConnectorAssociatedToProject,
    CONNECTORS_HAVING_IMPORT_DATA_BUTTON,
    CONNECTORS_WITHOUT_ACTUALIZE_DATA_BUTTON,
    CONNECTORS_HAVING_UPLOAD_FEATURE,
    deleteEditorConnection,
    isChooseLoginToConnectorProcessModalOpen,
    setIsChooseLoginToConnectorProcessModalOpen,
    redirectToConnectorLoginPage,
    createConnectionFormStep,
    setCreateConnectionFormStep,
    initOauthLoginToConnector,
    oauthError,
    setOauthError,
    isProjectCurrentlyRefreshing
  };

  const useMemoDeps = Object.values(memoizedValues).map(value => value);

  const value = useMemo(() => memoizedValues, useMemoDeps);

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
};
ProjectsProvider.propTypes = {
  children: oneOfType([node, func]).isRequired
};

export default ProjectsProvider;
